// Плашка вердикта — порт плеерного `FeedbackBanner` (academy-frontend
// `.../blocks/FeedbackBanner/`): подложка состояния от нижнего края экрана,
// заголовок по центру; авторское пояснение раскрывается по тапу (шеврон вверх —
// раскрыть, вниз — свернуть). Плашка лежит ПОВЕРХ низа экрана (`absolute`, у
// плеера `fixed`) и подлезает под CTA — содержимое квиза над ней не сдвигается.
// Показывается только в прохождении: до проверки вердикта нет.
import { useState } from 'react'
import { ChevronUp } from 'lucide-react'
import { blockTheme } from '@teach/ui'
import { cn } from '@/lib/utils'
import type { Verdict } from '../../preview/verdict'
import { pickMessages } from '@/i18n'
import type { ContentLang } from '@/features/editor/editorSlice'
import { messages } from './messages'

interface Props {
  verdict: Verdict | null
  /** Авторский текст (есть только у квиза) — пустая строка допустима. */
  text?: string
  /** Язык курса: вердикт — часть экрана ученика, плеер пишет его на языке курса. */
  lang: ContentLang
}

const q = blockTheme.quiz

// `accepted` — оценки нет (опрос без эталонного ответа): подача нейтральная,
// потому что зелёное «Правильно!» здесь было бы догадкой студии, а не фактом.
// В плеере такого вердикта нет — берём пару «выбранного» варианта той же палитры.
const VIEW: Record<Verdict, { bg: string; ink: string }> = {
  correct: { bg: q.successPanel, ink: q.successInk },
  incorrect: { bg: q.destructivePanel, ink: q.destructiveInk },
  accepted: { bg: q.primarySoft, ink: q.selectedInk },
}

// Низ плашки (70px) — место под CTA, которая рисуется поверх: 48px кнопки,
// 12px под ней (`pb-3` ряда в `PreviewShell`) и 10px воздуха над ней, как
// `calc(58px + 12px)` у плеера.
const PLATE =
  'absolute inset-x-0 bottom-0 rounded-t-2xl px-5 pb-[70px] pt-[18px] font-madefor animate-oa-sheetup'

const FeedbackPlate = ({ verdict, text, lang }: Props & { verdict: Verdict }) => {
  const m = pickMessages(messages, lang)
  const [expanded, setExpanded] = useState(false)
  const { bg, ink } = VIEW[verdict]
  const title = (
    <span className="block text-center text-[16px] font-semibold [overflow-wrap:anywhere]">
      {m[verdict]}
    </span>
  )
  return (
    <div className={PLATE} style={{ backgroundColor: bg, color: ink }}>
      {text ? (
        <button
          type="button"
          className="block w-full text-left"
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <ChevronUp
            className={cn(
              'mx-auto mb-0.5 block size-[22px] transition-transform duration-200',
              expanded ? 'rotate-180' : null,
            )}
            aria-hidden
          />
          {expanded ? (
            // Длинное пояснение переносится даже внутри слова и прокручивается в
            // плашке, а не уезжает за её край.
            <p className="max-h-40 overflow-y-auto whitespace-pre-line break-words text-[16px] font-medium leading-[1.35] [overflow-wrap:anywhere]">
              {text}
            </p>
          ) : (
            title
          )}
        </button>
      ) : (
        title
      )}
    </div>
  )
}

export const AnswerFeedback = ({ verdict, text, lang }: Props) =>
  // Плашка монтируется заново на каждый вердикт — раскрытое пояснение прошлой
  // попытки не переезжает в следующую (у плеера баннер тоже живёт от ответа).
  verdict === null ? null : <FeedbackPlate verdict={verdict} text={text} lang={lang} />
